import React, { useRef, useState } from 'react';
import { usePIM } from '../../context/PIMContext';
import { GhostButton } from '../ui/GhostButton';
import { Upload, RefreshCw, Trash2, Download, ZoomIn, ArrowUp, ArrowDown, Star } from 'lucide-react';

import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';
import { useToast } from '../../context/ToastContext';

const PicturesTab = () => {
    const { toast } = useToast();
    const { productData, productImages, fetchProductImages, activeCharge } = usePIM();
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

    // Filter Images by Active Charge
    const filteredImages = productImages.filter(img =>
        !activeCharge || img.charge_id === activeCharge.id
    );

    // Sorting
    const { items: sortedImages, requestSort, sortConfig } = useSortableData(filteredImages);

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const uploadFile = async (file) => {
        if (!productData.sku) {
            toast.error("Please select a product first.");
            return;
        }

        const formData = new FormData();
        formData.append('image', file);

        try {
            const res = await fetch(`/api/products/${productData.sku}/images`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: formData
            });

            if (!res.ok) {
                if (res.status === 503) {
                    const text = await res.text();
                    if (text === "NEXTCLOUD_CONNECTION_LOST") {
                        toast.error("Upload failed: No connection to Nextcloud storage. Please check if Drive Z: is mounted.");
                        return;
                    }
                }
                console.error(`Failed to upload ${file.name}`);
            }
        } catch (err) {
            console.error("Upload error:", err);
        }
    };

    const handleFiles = async (files) => {
        if (!files || files.length === 0) return;
        if (!productData.sku) {
            toast.error("Please select a product first.");
            return;
        }

        setUploading(true);
        setUploadProgress({ current: 0, total: files.length });

        try {
            const fileArray = Array.from(files);
            const uploadPromises = fileArray.map(async (file) => {
                await uploadFile(file);
                setUploadProgress(prev => ({ ...prev, current: prev.current + 1 }));
            });

            await Promise.all(uploadPromises);
            await fetchProductImages(productData.sku);
        } catch (err) {
            console.error("Batch upload error:", err);
        } finally {
            setUploading(false);
            setUploadProgress({ current: 0, total: 0 });
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleFileChange = (e) => {
        handleFiles(e.target.files);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        handleFiles(e.dataTransfer.files);
    };

    const handleDelete = async (id) => {
        // Confirmation removed as requested


        try {
            const res = await fetch(`/api/images/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (res.ok) {
                await fetchProductImages(productData.sku);
            } else {
                const errText = await res.text();
                toast.error(`Failed to delete image: ${errText || res.status}`);
            }
        } catch (err) {
            console.error("Delete error:", err);
        }
    };

    const handleSetMain = async (id) => {
        try {
            const res = await fetch(`/api/images/${id}/main`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (res.ok) {
                await fetchProductImages(productData.sku);
            } else {
                toast.error("Failed to set main image.");
            }
        } catch (err) {
            console.error("Set main error:", err);
        }
    };

    const formatSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const SortButton = ({ label, sortKey }) => {
        const isActive = sortConfig && sortConfig.key === sortKey;
        const direction = isActive ? sortConfig.direction : null;

        return (
            <button
                onClick={() => requestSort(sortKey)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium transition-colors ${isActive ? 'bg-stone-800 text-white' : 'text-stone-500 hover:text-stone-300 hover:bg-stone-800/50'}`}
            >
                {label}
                {direction === 'ascending' && <ArrowUp size={12} />}
                {direction === 'descending' && <ArrowDown size={12} />}
            </button>
        );
    };

    return (
        <div className="h-full flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <div className="flex gap-3 items-center">
                    <GhostButton onClick={handleUploadClick} disabled={uploading}>
                        <Upload size={16} /> {uploading ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...` : 'Upload Picture(s)'}
                    </GhostButton>
                    <GhostButton onClick={() => fetchProductImages(productData.sku)}>
                        <RefreshCw size={16} /> Refresh
                    </GhostButton>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        className="hidden"
                        accept="image/*"
                        multiple
                    />

                    <div className="h-6 w-px bg-stone-800 mx-2"></div>

                    <div className="flex items-center gap-1">
                        <span className="text-xs text-stone-500 mr-2">Sort by:</span>
                        <SortButton label="Name" sortKey="file_name" />
                        <SortButton label="Size" sortKey="file_size" />
                        <SortButton label="Date" sortKey="upload_date" />
                    </div>
                </div>

                {/* Drag and Drop Area - Top Right */}
                <div
                    className={`border-2 border-dashed rounded-lg px-4 py-2 flex items-center gap-3 transition-colors cursor-pointer relative overflow-hidden
                        ${isDragging ? 'border-blue-500 bg-blue-500/10' : 'border-stone-700 hover:border-stone-500 hover:bg-stone-800/50'}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handleUploadClick}
                    style={{ minWidth: '300px' }}
                >
                    {/* Progress Bar Background */}
                    {uploading && uploadProgress.total > 0 && (
                        <div
                            className="absolute left-0 top-0 bottom-0 bg-blue-900/20 transition-all duration-300 ease-out"
                            style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                        />
                    )}

                    <Upload size={20} className={`${isDragging ? 'text-blue-400' : 'text-stone-500'} z-10`} />
                    <div className="z-10 flex flex-col">
                        <p className={`text-xs font-medium ${isDragging ? 'text-blue-300' : 'text-stone-400'}`}>
                            {uploading
                                ? `Uploading... ${Math.round((uploadProgress.current / uploadProgress.total) * 100)}%`
                                : (isDragging ? 'Drop images here' : 'Drag & Drop images here')}
                        </p>
                        {uploading && (
                            <p className="text-[10px] text-stone-500">
                                {uploadProgress.current} of {uploadProgress.total} files
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className="bg-stone-900/50 border border-stone-700 rounded-xl p-6 flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-stone-950 [&::-webkit-scrollbar-thumb]:bg-stone-700 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-stone-600">
                {sortedImages.length === 0 ? (
                    <div className="text-center text-stone-500 mt-10">
                        No images uploaded for this product.
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {sortedImages.map((img) => (
                            <div key={img.id} className={`bg-stone-950 border rounded-lg overflow-hidden flex flex-col group transition-colors ${img.is_main ? 'border-yellow-500/50 shadow-[0_0_10px_rgba(234,179,8,0.1)]' : 'border-stone-800 hover:border-stone-600'}`}>
                                <div className="h-64 bg-black flex items-center justify-center p-4 relative">
                                    <img
                                        src={img.file_path}
                                        alt={img.file_name}
                                        className="max-w-full max-h-full object-contain"
                                    />
                                    {!!img.is_main && (
                                        <div className="absolute top-2 right-2 bg-yellow-500 text-black p-1 rounded-full shadow-lg">
                                            <Star size={12} fill="currentColor" />
                                        </div>
                                    )}
                                </div>
                                <div className="p-4 bg-stone-900 border-t border-stone-800">
                                    <div className="flex justify-between items-start mb-2">
                                        <span className="text-sm font-bold text-stone-300 truncate" title={img.file_name}>{img.file_name}</span>
                                    </div>
                                    <div className="text-xs text-stone-500 mb-4">
                                        <div>{formatSize(img.file_size)}</div>
                                        <div>{formatDate(img.upload_date)}</div>
                                    </div>
                                    <div className="flex justify-end gap-2">
                                        <button
                                            className={`p-2 rounded transition-colors ${img.is_main ? 'text-yellow-500 bg-yellow-500/10' : 'text-stone-400 hover:text-yellow-500 hover:bg-stone-800'}`}
                                            onClick={() => handleSetMain(img.id)}
                                            title={img.is_main ? "Main Image" : "Set as Main Image"}
                                        >
                                            <Star size={16} fill={img.is_main ? "currentColor" : "none"} />
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                window.location.href = `/api/charge-pictures/${img.id}/download`;
                                            }}
                                            className="p-2 rounded hover:bg-stone-800 text-stone-400 hover:text-white transition-colors"
                                            title="Download"
                                        >
                                            <Download size={16} />
                                        </button>
                                        <button
                                            className="p-2 rounded hover:bg-stone-800 text-stone-400 hover:text-white transition-colors"
                                            onClick={() => window.open(img.file_path, '_blank')}
                                            title="View Full Size"
                                        >
                                            <ZoomIn size={16} />
                                        </button>
                                        <button
                                            className="p-2 rounded hover:bg-red-900/30 text-stone-400 hover:text-red-500 transition-colors"
                                            onClick={() => handleDelete(img.id)}
                                            title="Delete"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PicturesTab;
